import React, { useState, useEffect } from 'react';
import {
    List,
    ListItem,
    ListItemText,
    IconButton,
    Collapse,
    Box,
    Button,
    Tooltip
} from '@mui/material';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import AddIcon from '@mui/icons-material/Add';
import { useTranslation } from 'react-i18next';

import { useChatSession } from '../contexts/ChatSessionContext';
import { useMessageHandler } from '../contexts/MessageContext';
import ChatSessionItem from './ChatSessionItem';
import ProjectItem from './ProjectItem';
import ProjectCreationDialog from './ProjectCreationDialog';
import ProjectContextMenu from './ProjectContextMenu';
import NewChatDialog from './NewChatDialog';
import { PROJECT_COLORS } from '../constants/appConstants';
import api from '../api/api';
import { getProjectItemStyles } from '../themes/migration-helpers';

const ProjectsList = ({onClose, onEngineChange}) => {
    const { t } = useTranslation();
    const {
        projects = [],
        expandedProjects = {},
        createProject,
        deleteProject,
        moveSessionToProject,
        updateProjectColor,
        toggleProjectExpanded,
        chatSessions = [],
        fetchProjects,
        fetchChatSessions,
        newChatSession,
        createChatInProject,
    } = useChatSession();

    const {
        currentSession,
        showDialog,
        setMessages,
        setCurrentSession,
        isInteracting,
    } = useMessageHandler();

    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const [projectMenuAnchor, setProjectMenuAnchor] = useState(null);
    const [activeProjectId, setActiveProjectId] = useState(null);
    const [projectsVisible, setProjectsVisible] = useState(true);
    const [newChatDialogOpen, setNewChatDialogOpen] = useState(false);
    const [targetProjectId, setTargetProjectId] = useState(null);
    const [isCreatingChat, setIsCreatingChat] = useState(false);

    // expandedProjects state is managed by the ChatSessionContext

    const handleOpenProjectMenu = (event, projectId) => {
        event.stopPropagation();
        setProjectMenuAnchor(event.currentTarget);
        setActiveProjectId(projectId);
    };

    const handleCloseProjectMenu = () => {
        setProjectMenuAnchor(null);
        setActiveProjectId(null);
    };

    const handleCreateNewChat = (projectId) => {
        setTargetProjectId(projectId);
        setNewChatDialogOpen(true);
    };

    const handleCloseNewChatDialog = () => {
        setNewChatDialogOpen(false);
        setTargetProjectId(null);
        setIsCreatingChat(false);
    };

    const handleCreateChatInProject = async (projectId, chatName) => {
        try {
            setIsCreatingChat(true);

            // Use the proper createChatInProject function from context
            const newSession = await createChatInProject(projectId, chatName);

            if (newSession) {
                // Clear current messages and set the new session as current
                setMessages([]);
                setCurrentSession(newSession.sessionId);

                // Refresh projects to show the new session
                await fetchProjects();
                
                // Close the dialog
                handleCloseNewChatDialog();

                // Close the menu panel on mobile
                const isMobile = window.innerWidth < 600;
                if (isMobile && onClose) {
                    onClose();
                }
            }

        } catch (error) {
            console.error('Error creating new chat in project:', error);
        } finally {
            setIsCreatingChat(false);
        }
    };

    const renderSession = (session, isInProject, projectId) => (
        <div
            key={session.sessionId}
            style={{ display: 'flex', alignItems: 'center' }}
        >
            <ChatSessionItem
                session={session}
                isHovered={false}
                isCurrentSession={currentSession === session.sessionId}
                isInProject={isInProject}
                projectId={projectId}
                onClose={onClose}
                onEngineChange={onEngineChange}
            />
        </div>
    );

    // Handle project reordering
    const handleMoveProject = async (projectId, direction) => {
        try {
            // Find current project index
            const projectIndex = projects.findIndex(p => p.id === projectId);
            if (projectIndex === -1) return;

            let newPosition;
            if (direction === 'up') {
                if (projectIndex === 0) return; // Already at the top
                newPosition = projectIndex - 1;
            } else {
                if (projectIndex === projects.length - 1) return; // Already at the bottom
                newPosition = projectIndex + 1;
            }

            // Save new order to database
            await api.put(`${process.env.REACT_APP_API_BASE_URL}/api/chat/projects/${projectId}/order`, {
                newPosition
            });

            // Refresh projects list to get updated order
            await fetchProjects();

            // Close menu
            setProjectMenuAnchor(null);
            setActiveProjectId(null);

        } catch (error) {
            console.error('Error reordering project:', error);
        }
    };

    const handleCreateProject = async (projectName, colorValue) => {
        if (typeof createProject === 'function') {
            await createProject(projectName, colorValue);
            setCreateDialogOpen(false);
        } else {
            console.error('createProject function is not available');
            alert('Cannot create project: function not available');
        }
    };

    const handleDeleteProject = async () => {
        // Close the project menu
        handleCloseProjectMenu();

        // Get project name for the confirmation message
        const projectToDelete = projects.find(p => p.id === activeProjectId);
        if (!projectToDelete) return;

        // Show confirmation dialog
        if (typeof showDialog === 'function') {
            showDialog(
                t('projects.delete.title'),
                t('projects.delete.confirmWithName', { name: projectToDelete.name }),
                async () => {
                    if (activeProjectId && typeof deleteProject === 'function') {
                        await deleteProject(activeProjectId);
                    }
                }
            );
        }
    };

    const handleChangeColor = async (colorId) => {
        if (activeProjectId && typeof updateProjectColor === 'function') {
            const colorValue = PROJECT_COLORS.find(c => c.id === colorId)?.value;
            await updateProjectColor(activeProjectId, colorValue);
            handleCloseProjectMenu();
        }
    };

    const getProjectSessions = (projectId) => {
        if (!projects || !Array.isArray(projects) || !chatSessions || !Array.isArray(chatSessions)) {
            return [];
        }

        const project = projects.find(p => p && p.id === projectId);
        if (!project || !project.sessions || !Array.isArray(project.sessions)) {
            return [];
        }

        return project.sessions
            .map(sessionId => chatSessions.find(s => s && s.sessionId === sessionId))
            .filter(Boolean) // Remove any undefined values
            .sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
    };

    return (
        <>
            <Box sx={{
                mb: 1,
                mt: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
            }}>
                <Button
                    variant="outlined"
                    onClick={() => setProjectsVisible(!projectsVisible)}
                    startIcon={projectsVisible ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    sx={(theme) => {
                        const projectStyles = getProjectItemStyles(theme);
                        return {
                            textTransform: 'none',
                            justifyContent: 'flex-start',
                            flexGrow: 1,
                            mr: 1,
                            ...projectStyles,
                        };
                    }}
                >
                    {t('projects.title')}
                </Button>
                <Tooltip title={t('projects.create')}>
                    <IconButton
                        size="small"
                        onClick={() => setCreateDialogOpen(true)}
                        data-testid="new-project-button"
                        sx={(theme) => {
                            const projectStyles = getProjectItemStyles(theme);
                            return {
                                borderRadius: '4px',
                                ...projectStyles,
                                padding: '4px',
                                width: '32px',
                                height: '32px',
                            };
                        }}
                    >
                        <AddIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            </Box>

            <Collapse in={projectsVisible} timeout="auto">
                <List sx={{ width: '100%', p: 0, mb: 0 }}>
                    {projects && projects.length > 0 ? (
                        projects.map(project => (
                            <ProjectItem
                                key={project.id}
                                project={project}
                                isExpanded={expandedProjects[project.id]}
                                onToggleExpand={toggleProjectExpanded}
                                onOpenMenu={handleOpenProjectMenu}
                                onMoveSession={moveSessionToProject}
                                projectSessions={getProjectSessions(project.id)}
                                renderSession={renderSession}
                                onCreateNewChat={handleCreateNewChat}
                            />
                        ))
                    ) : (
                        <ListItem>
                            <ListItemText
                                primary={t('projects.noProjects')}
                                primaryTypographyProps={{
                                    sx: { fontSize: '0.75rem', color: 'text.secondary', fontStyle: 'italic' }
                                }}
                            />
                        </ListItem>
                    )}
                </List>
            </Collapse>

            {/* Project Creation Dialog */}
            <ProjectCreationDialog
                open={createDialogOpen}
                onClose={() => setCreateDialogOpen(false)}
                onCreateProject={handleCreateProject}
            />

            {/* Project Context Menu */}
            <ProjectContextMenu
                anchorEl={projectMenuAnchor}
                open={Boolean(projectMenuAnchor)}
                onClose={handleCloseProjectMenu}
                onDeleteProject={handleDeleteProject}
                onChangeColor={(colorId) => handleChangeColor(colorId)}
                onMoveUp={() => handleMoveProject(activeProjectId, 'up')}
                onMoveDown={() => handleMoveProject(activeProjectId, 'down')}
                canMoveUp={activeProjectId !== null && projects.findIndex(p => p.id === activeProjectId) > 0}
                canMoveDown={activeProjectId !== null && projects.findIndex(p => p.id === activeProjectId) < projects.length - 1}
            />

            {/* New Chat in Project Dialog */}
            <NewChatDialog
                open={newChatDialogOpen}
                onClose={handleCloseNewChatDialog}
                onCreateChat={(chatName) => handleCreateChatInProject(targetProjectId, chatName)}
                projectName={projects.find(p => p.id === targetProjectId)?.name || ''}
                isCreating={isCreatingChat}
            />
        </>
    );
};

export default ProjectsList;